import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import {
  doc,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import PayPalButtonChangePlan from "./PayPalButtonChangePlan";

export default function ChangePlanPromo({
  isOpen,
  onClose,
  promoProfiles,
  userId,
  clientId,
  exchangeRate,
  backendUrl,
  plans,
}) {
  const [transactionData, setTransactionData] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [paypalError, setPaypalError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [readyToRenderPayPal, setReadyToRenderPayPal] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const getLatestTransactionData = async (profileId) => {
    try {
      const profileRef = doc(db, `users/${userId}/promoProfiles/${profileId}`);
      const profileDoc = await getDoc(profileRef);
      let centralizedExpirationDate = null;
      let amountUSD = null;
      let planType = null;
      let postsPerMonth = null;

      if (profileDoc.exists()) {
        const profileData = profileDoc.data();
        centralizedExpirationDate = profileData.centralizedExpirationDate?.toDate() || null;
        amountUSD = profileData.amountUSD || null;
        planType = profileData.planType || null;
        postsPerMonth = profileData.postsPerMonth || null;
      }

      const transactionsQuery = query(
        collection(db, `users/${userId}/promoProfiles/${profileId}/transactions`),
        orderBy("expirationDate", "desc"),
        limit(1)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);

      if (!transactionsSnapshot.empty) {
        const latestTransaction = transactionsSnapshot.docs[0].data();
        return {
          expirationDate: centralizedExpirationDate || latestTransaction.expirationDate?.toDate() || null,
          amountUSD: amountUSD || latestTransaction.amount || null,
          postsPerMonth: postsPerMonth || latestTransaction.postsPerMonth || null,
          planType: planType || latestTransaction.planType || null,
        };
      }

      return {
        expirationDate: centralizedExpirationDate || null,
        amountUSD: amountUSD || null,
        postsPerMonth: postsPerMonth || null,
        planType: planType || null,
      };
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    const fetchTransactionData = async () => {
      if (!isOpen || !userId || !promoProfiles.length) return;

      const data = {};
      for (const profile of promoProfiles) {
        const latestData = await getLatestTransactionData(profile.id);
        data[profile.id] = latestData;
      }
      setTransactionData(data);
    };

    fetchTransactionData();
  }, [isOpen, userId, promoProfiles]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPlan(null);
      setActiveProfileId(null);
      setSuccessMessage(null);
      setPaypalError(null);
      setProcessing(false);
      setReadyToRenderPayPal(false);
      setOrderId(null);
    }
  }, [isOpen]);

  const formatExpirationDate = (date) => {
    if (!date) return "No disponible";
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setPaypalError(null);
    setReadyToRenderPayPal(false);
    setOrderId(null);
    setActiveProfileId(null);
  };

  const handleChangePlan = async (profile) => {
    if (processing || (activeProfileId === profile.id && readyToRenderPayPal) || !selectedPlan) return;

    setProcessing(true);
    setPaypalError(null);
    setActiveProfileId(profile.id);
    setReadyToRenderPayPal(false);
    setOrderId(null);

    try {
      const amountUSD = selectedPlan.priceDOP / exchangeRate;
      const response = await fetch(`${backendUrl}/api/renewals/create-profile-change-plan-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: Number(amountUSD.toFixed(2)),
          userId,
          planType: selectedPlan.id,
          profileId: profile.id,
        }),
      });

      const data = await response.json();
      if (data.orderId) {
        setOrderId(data.orderId);
        setReadyToRenderPayPal(true);
      } else {
        setPaypalError(data.error || "Error al crear la orden de pago.");
        setActiveProfileId(null);
      }
    } catch (error) {
      setPaypalError("Error al conectar con el servidor.");
      setActiveProfileId(null);
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = (profile, orderDetails) => {
    // El servidor ya escribió la transacción y actualizó el perfil en Firestore
    // durante la captura. Aquí solo actualizamos el estado local de la UI.
    const planType = selectedPlan.id;
    const postsPerMonth = selectedPlan.postsPerMonth;
    const amountUSD = selectedPlan.priceDOP / exchangeRate;

    setTransactionData((prev) => ({
      ...prev,
      [profile.id]: {
        ...prev[profile.id],
        expirationDate: orderDetails.expirationDate ? new Date(orderDetails.expirationDate) : new Date(),
        amountUSD: Number(amountUSD.toFixed(2)),
        postsPerMonth,
        planType,
      },
    }));

    setSuccessMessage(`¡Cambio de plan exitoso para el perfil ${profile.name}!`);
    setSelectedPlan(null);
    setReadyToRenderPayPal(false);
    setOrderId(null);
    setActiveProfileId(null);
    setTimeout(() => {
      setSuccessMessage(null);
      onClose();
      window.location.reload();
    }, 2000);
  };

  const handlePaymentError = (error) => {
    setPaypalError(error.message || "Error al procesar el pago. Por favor, intenta de nuevo.");
    setProcessing(false);
    setReadyToRenderPayPal(false);
    setOrderId(null);
    setActiveProfileId(null);
  };

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => !processing && onClose()}>
        <div onClick={handleModalClick}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 dark:bg-black/80" />
          </Transition.Child>

          <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="scale-95 opacity-0"
              enterTo="scale-100 opacity-100"
              leave="ease-in duration-200"
              leaveFrom="scale-100 opacity-100"
              leaveTo="scale-95 opacity-0"
            >
              <Dialog.Panel className="w-full max-w-lg sm:max-w-2xl bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl p-6 sm:p-8 shadow-2xl transition-all">
                <button
                  onClick={() => !processing && onClose()}
                  className="absolute top-4 right-4 text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-300 text-2xl font-bold transition-transform hover:scale-125"
                >
                  ×
                </button>

                <Dialog.Title className="text-3xl sm:text-4xl font-extrabold text-center mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-300 dark:to-purple-400">
                  Cambiar Tipo de Plan
                </Dialog.Title>

                {successMessage && (
                  <p className="text-center text-green-500 font-semibold text-lg mb-6 bg-green-100/50 dark:bg-green-900/50 py-2 rounded-lg z-50">
                    {successMessage}
                  </p>
                )}

                {paypalError && (
                  <p className="text-center text-red-500 font-semibold text-lg mb-6 bg-red-100/50 dark:bg-red-900/50 py-2 rounded-lg z-50">
                    {paypalError}
                  </p>
                )}

                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4 text-center">
                    Selecciona el Nuevo Plan
                  </h2>
                  <div className="grid gap-6 sm:grid-cols-2">
                    {plans.map((plan) => (
                      <div
                        key={plan.id}
                        onClick={() => handlePlanSelect(plan)}
                        className={`cursor-pointer p-6 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 ${
                          selectedPlan?.id === plan.id
                            ? "border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/50 dark:to-purple-900/50"
                            : "border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                          {plan.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-2">
                          <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                            {plan.postsPerMonth}
                          </span>{" "}
                          posts por mes
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 mb-2">
                          RD$ <span className="font-bold text-lg">{plan.priceDOP.toFixed(2)}</span> / mes
                        </p>
                        <p className="text-gray-600 dark:text-gray-300 mb-2">
                          USD <span className="font-bold text-lg">${(plan.priceDOP / exchangeRate).toFixed(2)}</span> / mes
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {promoProfiles.length === 0 ? (
                  <div className="text-center text-gray-600 dark:text-gray-300">
                    No tienes perfiles promocionales para cambiar de plan.
                  </div>
                ) : (
                  <div className="space-y-6">
                    {promoProfiles.map((profile) => {
                      const t = transactionData[profile.id];
                      const isExpired =
                        !t?.expirationDate || t.expirationDate < new Date();
                      const amountUSD = selectedPlan ? selectedPlan.priceDOP / exchangeRate : (t?.amountUSD || (profile.planType === "basic" ? 500 / exchangeRate : 1000 / exchangeRate));
                      const amountDOP = selectedPlan ? selectedPlan.priceDOP : (t?.amountUSD ? Math.round(t.amountUSD * exchangeRate) : (profile.planType === "basic" ? 500 : 1000));

                      return (
                        <div
                          key={profile.id}
                          className={`p-6 rounded-2xl border-2 ${
                            isExpired ? "border-gray-200 dark:border-gray-700" : "border-gray-300 dark:border-gray-600 opacity-50"
                          } bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/50 dark:to-purple-900/50`}
                          onClick={handleModalClick}
                        >
                          {profile.profilePhotoUrl && (
                            <img
                              src={profile.profilePhotoUrl}
                              alt={profile.name}
                              className="w-16 h-16 rounded-full mb-4 mx-auto border-2 border-white dark:border-gray-900 shadow-sm"
                            />
                          )}
                          <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2 text-center">
                            {profile.name}
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300 mb-2 text-center">{profile.description}</p>
                          <p className="text-gray-600 dark:text-gray-300 mb-2">
                            Plan Actual: {(t?.planType || profile.planType) === "basic" ? "Básico" : "Premium"} ({t?.postsPerMonth || (profile.planType === "basic" ? 5 : 10)} posts/mes)
                          </p>
                          <p className="text-gray-600 dark:text-gray-300 mb-2">
                            Expira: {formatExpirationDate(t?.expirationDate)}{" "}
                            {!isExpired && <span className="text-green-500 font-semibold">(Activo)</span>}
                          </p>
                          <p className="text-gray-600 dark:text-gray-300 mb-2">
                            Tags: {profile.tags?.join(", ") || "Ninguno"}
                          </p>
                          {selectedPlan && (
                            <p className="text-gray-600 dark:text-gray-300 mb-4">
                              Costo Nuevo Plan: RD${amountDOP} (${Number(amountUSD.toFixed(2))} USD)
                            </p>
                          )}
                          {isExpired && selectedPlan ? (
                            readyToRenderPayPal && orderId && activeProfileId === profile.id ? (
                              <PayPalButtonChangePlan
                                key={`${orderId}-${profile.id}`}
                                clientId={clientId}
                                price={Number(amountUSD.toFixed(2))}
                                userId={userId}
                                planType={selectedPlan.id}
                                profileId={profile.id}
                                orderId={orderId}
                                onSuccess={(orderDetails) => handlePaymentSuccess(profile, orderDetails)}
                                onError={handlePaymentError}
                              />
                            ) : (
                              <button
                                onClick={() => handleChangePlan(profile)}
                                disabled={processing || (readyToRenderPayPal && activeProfileId !== profile.id) || !selectedPlan}
                                className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-bold text-lg hover:from-indigo-600 hover:to-purple-700 transition-all disabled:opacity-50"
                              >
                                {processing && activeProfileId === profile.id ? (
                                  <span className="flex items-center justify-center gap-3">
                                    <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    Procesando...
                                  </span>
                                ) : (
                                  "Cambiar Plan"
                                )}
                              </button>
                            )
                          ) : (
                            <p className="text-center text-gray-600 dark:text-gray-300">
                              {isExpired ? "Selecciona un plan para continuar." : "Este perfil está activo y no puede cambiarse hasta que expire."}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}